import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/config/languages';

/**
 * Locale negotiation (#2109 R1 lane L2; research §2.2 rules 5–7, 12).
 *
 * Precedence: `?lang=` (the only thing a shared link can carry) → the stored
 * preference (`User.preferences.language` when signed in, `localStorage`
 * for an anonymous visitor) → `navigator.languages` on a first anonymous
 * visit → `en`. The browser step may only pick a code in `browserEligible`
 * (rulings brief B14: an unreviewed language is never anyone’s default);
 * `?lang=` and the stored preference are not filtered, because the person
 * chose them. Nothing here is ever persisted — only the switcher writes.
 */

/**
 * The UI catalog locales, keyed by the language subtag an alias resolves to.
 * `fil` is a valid stored preference for staff↔family chat translation but
 * has no UI catalog, so it has no row here and resolves to `en`.
 */
const UI_LANGUAGE_SUBTAGS: Record<string, LanguageCode> = {
  en: 'en',
  es: 'es',
  pt: 'pt' as LanguageCode,
  fr: 'fr' as LanguageCode,
  zh: 'zh',
};

/**
 * `bcp47` per declaration code until L1 lands `bcp47` on
 * `SUPPORTED_LANGUAGES` (agents/shared/languages.json is the one
 * declaration; this is read-through, not a second table — delete the
 * fallback once every entry carries the field).
 */
const BCP47_UNTIL_L1: Record<string, string> = {
  en: 'en',
  es: 'es',
  pt: 'pt-BR',
  fr: 'fr',
  zh: 'zh-Hans',
  fil: 'fil',
};

/** The declaration codes that have a UI catalog, in declaration order. */
export function uiLocales(supported: readonly string[] = SUPPORTED_LANGUAGES.map((l) => l.code)): LanguageCode[] {
  return supported.filter((code): code is LanguageCode => code in UI_LANGUAGE_SUBTAGS);
}

/** The BCP 47 tag for `<html lang>`, `Intl.*` and `:lang()` — never the declaration code. */
export function bcp47For(code: string): string {
  const entry = SUPPORTED_LANGUAGES.find((l) => l.code === code) as { bcp47?: string } | undefined;
  return entry?.bcp47 ?? BCP47_UNTIL_L1[code] ?? code;
}

interface ParsedTag {
  language: string;
  script?: string;
  region?: string;
}

function parse(tag: string): ParsedTag | null {
  if (typeof tag !== 'string' || !tag.trim()) return null;
  try {
    const [canonical] = Intl.getCanonicalLocales(tag.trim());
    if (!canonical) return null;
    const locale = new Intl.Locale(canonical);
    return { language: locale.language, script: locale.script, region: locale.region };
  } catch {
    return null;
  }
}

const ZH_HANS_REGIONS = new Set(['CN', 'SG', 'MY']);

/**
 * The explicit alias map (research §2.2 rule 6), applied AFTER
 * canonicalisation: `zh`, `zh-CN`, `zh-SG`, `zh-MY`, `zh-Hans-*` → `zh`;
 * `pt`, `pt-PT`, `pt-*` → `pt`; `es-*` → `es`; `fr-*` → `fr`. Anything
 * unresolved — including `zh-Hant`, `zh-TW`, `zh-HK` — returns null, because
 * Traditional Chinese is not offered and Simplified is not an acceptable
 * substitute (rulings brief B17). Returns the language subtag to look up.
 */
function aliasSubtag(parsed: ParsedTag): string | null {
  if (parsed.language === 'zh') {
    if (parsed.script === 'Hans') return 'zh';
    if (parsed.script) return null;
    if (!parsed.region || ZH_HANS_REGIONS.has(parsed.region)) return 'zh';
    return null;
  }
  return parsed.language;
}

/**
 * RFC 4647 lookup by right-truncation after the alias step. `pt-BR` and
 * `pt-PT` both resolve to `pt`, which always formats as pt-BR (rule 12).
 */
export function matchLocale(tag: string | null | undefined, supported: readonly string[]): LanguageCode | null {
  if (!tag) return null;
  const parsed = parse(tag);
  if (!parsed) return null;
  const subtag = aliasSubtag(parsed);
  if (!subtag) return null;
  const code = UI_LANGUAGE_SUBTAGS[subtag];
  return code && supported.includes(code) ? code : null;
}

export interface ResolveLocaleInput {
  /** The `?lang=` query value, raw. */
  query?: string | null;
  /** The stored preference: profile field when signed in, `localStorage` otherwise. */
  stored?: string | null;
  /** `navigator.languages`, consulted only when nothing above resolved. */
  navigatorLanguages?: readonly string[] | null;
  /** Codes the browser hint may pick — languages whose shell is `approved`. */
  browserEligible: readonly LanguageCode[];
  /** The declaration codes; defaults to `SUPPORTED_LANGUAGES`. Tests inject the five-code set. */
  supported?: readonly string[];
}

export function resolveLocale(input: ResolveLocaleInput): LanguageCode {
  const supported = input.supported ?? SUPPORTED_LANGUAGES.map((l) => l.code);
  const fromQuery = matchLocale(input.query, supported);
  if (fromQuery) return fromQuery;
  const fromStored = matchLocale(input.stored, supported);
  if (fromStored) return fromStored;
  for (const tag of input.navigatorLanguages ?? []) {
    const code = matchLocale(tag, supported);
    if (code && input.browserEligible.includes(code)) return code;
  }
  return 'en';
}
